import { Component, Suspense, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EmptyState } from './ui/empty-state'
import { Button } from './ui/button'

function LoadFailed() {
  const { t } = useTranslation()
  return (
    <div className="grid min-h-[50vh] place-items-center p-6">
      <EmptyState
        title={t('pageLoadFailed.title')}
        description={t('pageLoadFailed.description')}
        action={<Button onClick={() => window.location.reload()}>{t('pageLoadFailed.reload')}</Button>}
      />
    </div>
  )
}

class Catch extends Component<{ resetKey: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidUpdate(prev: { resetKey: string }) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false })
  }

  render() {
    return this.state.failed ? <LoadFailed /> : this.props.children
  }
}

// Where a lazy page loads: shows the fallback meanwhile and an error state if the page cannot load.
export default function PageBoundary({ fallback, children }: { fallback: ReactNode; children: ReactNode }) {
  const { pathname } = useLocation()
  return (
    <Catch resetKey={pathname}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </Catch>
  )
}

// The loading line of the boot screen, inside the content area: the shell around it stays.
export function PageLoading() {
  const { t } = useTranslation()
  return (
    <div role="status" aria-live="polite" className="grid h-full min-h-[50vh] place-items-center">
      <div className="boot-line" />
      <span className="sr-only">{t('loading')}</span>
    </div>
  )
}
